<template>
  <div>
    <Head :title="displayName" />
    <h1 class="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-3xl font-bold">
      <Link
        class="text-indigo-500 hover:text-indigo-800"
        :href="servicesRoute"
      >
        Usługi
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      <span>{{ displayName }}</span>
      <medfile-identifier-badge
        label="remote_uuid"
        :value="service.remote_uuid"
      />
    </h1>
    <div class="w-full">
      <service-form
        v-model="form"
        v-model:editing-locale="editingLocale"
        :available-specialists="available_specialists"
        :category-options="category_options"
        :show-locale-switcher="false"
        :show-active-toggle="false"
        :lock-synced-fields="Boolean(service.remote_uuid)"
        @submit="submit"
      >
        <footer
          class="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-100 bg-white/85 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] backdrop-blur-md lg:left-64"
        >
          <div class="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3 sm:px-6 lg:px-8">
            <record-update-dates
              compact
              :synced-at="service.synced_at"
              :updated-at="service.updated_at"
            />
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <locale-editor-switch v-model="editingLocale" />
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <div class="flex shrink-0 items-center gap-2.5">
              <span class="text-sm font-medium text-gray-700">Aktywna</span>
              <toggle-switch
                v-model="form.service.active"
                :errors="form.errors['service.active'] || []"
              />
            </div>
            <div class="ml-auto flex shrink-0 items-center gap-2">
              <button
                class="inline-flex h-9 min-w-24 items-center justify-center gap-1.5 rounded-lg bg-red-50 px-4 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                type="button"
                title="Usuń"
                @click="destroy"
              >
                <icon name="trash" class="h-3.5 w-3.5 stroke-current" />
                Usuń
              </button>
              <loading-button
                :loading="form.processing"
                class="inline-flex h-9 min-w-24 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                type="submit"
              >
                Zapisz zmiany
              </loading-button>
            </div>
          </div>
        </footer>
      </service-form>

      <version-history
        class="mt-8 pb-28"
        :versions="versions"
      />
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Head, Link, router, useForm } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import MedfileIdentifierBadge from '@/Shared/MedfileIdentifierBadge.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import RecordUpdateDates from '@/Shared/RecordUpdateDates.vue';
import LocaleEditorSwitch from '@/Shared/LocaleEditorSwitch.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import VersionHistory from '@/Shared/VersionHistory.vue';
import ServiceForm from './Form.vue';
import omit from 'lodash/omit';
import { createServiceRouteHelper } from '@/utils/createServiceRouteHelper';

const { proxy } = getCurrentInstance();
const routes = proxy.$routes;
const editingLocale = ref('pl');

const props = defineProps({
  service: {
    type: Object,
    required: true,
  },
  // eslint-disable-next-line vue/prop-name-casing
  available_specialists: {
    type: Array,
    default: () => [],
  },
  // eslint-disable-next-line vue/prop-name-casing
  category_options: {
    type: Array,
    default: () => [],
  },
  versions: {
    type: Array,
    default: () => [],
  },
});

const serviceData = omit(props.service, 'id', 'slug', 'remote_uuid', 'data_pl', 'data_en');

const emptyLocale = () => ({
  name: '',
  description: '',
  important_information: [],
  faq: [],
});

const form = useForm({
  service: {
    ...serviceData,
    specialist_ids: serviceData.specialist_ids || [],
    data_pl: { ...emptyLocale(), ...(props.service.data_pl || {}) },
    data_en: { ...emptyLocale(), ...(props.service.data_en || {}) },
  },
});

const displayName = computed(
  () => form.service.data_pl.name || form.service.data_en.name || '',
);

const serviceRoutes = computed(() => createServiceRouteHelper(null, routes));

const servicesRoute = computed(() => serviceRoutes.value.index());

const submit = () => {
  form.put(serviceRoutes.value.update(props.service.id));
};

const destroy = () => {
  if (confirm('Czy na pewno chcesz usunąć tę usługę?')) {
    router.delete(serviceRoutes.value.destroy(props.service.id));
  }
};
</script>
